import { Box, Typography, styled } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { PieChartProps } from '@mui/x-charts';
import React, { useMemo, useState } from 'react';

import PieChart from 'components/charts/pie-chart';
import ChartLegend from 'components/charts/chart-legend';
import { PIE_CHART_GEOMETRY, PIE_EMPHASIS } from 'components/charts/constants/chart-defaults';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { MOTION_TOKENS } from 'views/playground/features/data-lab/constants/motion-tokens';
import { SelectableChartProps } from 'views/playground/features/data-lab/types/selectable-chart.type';
import { CategoryPoint } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

// The donut band is thin
// (`PIE_CHART_GEOMETRY`'s `innerRadius: '62%'` to `outerRadius: '88%'`), so a
// label does not fit on a narrow arc. `arcLabelMinAngle` is the package's own
// suppression mechanism; below it a slice gets no label at all rather than one
// that overflows or collides with its neighbours. The legend (added below) is
// what still identifies that slice, which is also why the two must not carry
// identical text: the legend covers exactly the slices that lose their label.
const ARC_LABEL_MIN_ANGLE = 20;

type Props = SelectableChartProps &
  Pick<PieChartProps, 'height' | 'skipAnimation'> & {
    data: CategoryPoint[];
  };

// The donut's centre is dead space and the best real estate on the chart: an
// absolutely positioned overlay, not an SVG element, driven by `onHighlightChange`
// rather than mouse maths.
const CenterOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Keying by the hovered identity replays the fade when the center content changes.
const CenterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  animation: `${fadeIn} ${MOTION_TOKENS.select.duration}ms ${MOTION_TOKENS.select.easing}`,
}));

const CenterValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 28,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: chartPalette.text.primary,
}));

const CenterLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 13,
  color: chartPalette.text.secondary,
}));

const DistributionDonutChart: React.FC<Props> = ({ data, height = 320, skipAnimation, selectedId, onSelect }) => {
  const { formatKg } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation(skipAnimation);
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();
  const [hovered, setHovered] = useState<{ label: string; value: number } | null>(null);
  const coloredData = useMemo(
    () => data.map((point, index) => ({ ...point, color: palette.series.categorical[index % palette.series.categorical.length] })),
    [data, palette.series.categorical]
  );
  // Colors are assigned before filtering so a slice keeps its color when others are hidden.
  const { hiddenIds, toggle, visibleSeries: visibleData } = useSeriesVisibility(coloredData);
  const allSlicesHidden = coloredData.length > 0 && visibleData.length === 0;
  const total = visibleData.reduce((sum, point) => sum + point.value, 0);

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <Box position="relative" height={height}>
        <PieChart
          height={height}
          skipAnimation={chartSkipAnimation}
          hideLegend
          slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay, tooltip: DataLabChartTooltip }}
          series={[
            {
              ...PIE_CHART_GEOMETRY,
              ...PIE_EMPHASIS,
              data: visibleData.map((point) => ({ id: point.id, label: point.label, value: point.value, color: point.color })),
              valueFormatter: (value) => formatKg(value.value),
              // The legend carries the category name, so the arc label shows
              // the value.
              arcLabel: (item) => formatKg(item.value),
              arcLabelMinAngle: ARC_LABEL_MIN_ANGLE,
            },
          ]}
          onItemClick={(_event, pieItemIdentifier) => {
            const category = visibleData[pieItemIdentifier.dataIndex];
            if (!category) return;
            onSelect?.(category.id === selectedId ? null : category.id);
          }}
          onHighlightChange={(highlightedItem) => {
            if (!highlightedItem || highlightedItem.dataIndex === undefined) {
              setHovered(null);
              return;
            }
            const category = visibleData[highlightedItem.dataIndex];
            setHovered(category ? { label: category.label, value: category.value } : null);
          }}
        />
        {!allSlicesHidden && (
          <CenterOverlay>
            <CenterContent key={hovered?.label ?? 'total'}>
              <CenterValue chartPalette={palette}>{formatKg(hovered?.value ?? total)}</CenterValue>
              <CenterLabel chartPalette={palette}>{hovered?.label ?? 'Total'}</CenterLabel>
            </CenterContent>
          </CenterOverlay>
        )}
      </Box>
      {legendVisible && (
        // Category names live in the legend because the arcs show values.
        <ChartLegend
          items={coloredData.map((point) => ({ id: point.id, label: point.label, color: point.color }))}
          hiddenIds={hiddenIds}
          onToggle={toggle}
        />
      )}
    </>
  );
};

export default DistributionDonutChart;
